import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, Car, Plus, Pencil, Trash2, AlertTriangle, Calendar, Gauge, Building, MapPin } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useTranslation } from "../contexts/I18nContext";

export default function AutomezziPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditAutomezzi = canEdit("automezzi");
  
  const [automezzi, setAutomezzi] = useState([]);
  const [centriCosto, setCentriCosto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [savingAutomezzo, setSavingAutomezzo] = useState(false);
  const [deletingAutomezzo, setDeletingAutomezzo] = useState(false);
  
  const [form, setForm] = useState({
    marca: "",
    modello: "",
    targa: "",
    n_posti: "",
    centro_costo_id: "",
    autista_principale: "",
    note: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [automezziRes, ccRes] = await Promise.all([
        api.get(`${prefix}/automezzi`),
        api.get(`${prefix}/cost-centers`)
      ]);
      setAutomezzi(automezziRes.data);
      setCentriCosto(ccRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      marca: "",
      modello: "",
      targa: "",
      n_posti: "",
      centro_costo_id: "",
      autista_principale: "",
      note: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setForm({
      marca: item.marca || "",
      modello: item.modello || "",
      targa: item.targa || "",
      n_posti: item.n_posti ? String(item.n_posti) : "",
      centro_costo_id: item.centro_costo_id || "",
      autista_principale: item.autista_principale || "",
      note: item.note || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.marca || !form.modello || !form.targa) {
      handleApiError({ message: t("errors.requiredBrandModelPlate") });
      return;
    }
    
    setSavingAutomezzo(true);
    try {
      const payload = {
        marca: form.marca,
        modello: form.modello,
        targa: form.targa.toUpperCase(),
        n_posti: form.n_posti ? parseInt(form.n_posti) : null,
        centro_costo_id: form.centro_costo_id && form.centro_costo_id !== "__none__" ? form.centro_costo_id : null,
        autista_principale: form.autista_principale || null,
        note: form.note || null
      };
      
      if (editingId) {
        await api.put(`${getApiPrefix()}/automezzi/${editingId}`, payload);
        showSuccess(t("success.vehicleUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/automezzi`, payload);
        showSuccess(t("success.vehicleCreated"));
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSavingAutomezzo(false);
    }
  };

  const handleDelete = async () => {
    setDeletingAutomezzo(true);
    try {
      await api.delete(`${getApiPrefix()}/automezzi/${deleteDialog.item.id}`);
      showSuccess(t("success.vehicleDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeletingAutomezzo(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    const prefix = getApiPrefix();
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/automezzi/${id}`);
        deleted++;
      } catch (err) {
        
      }
    }
    showSuccess(`${deleted} automezzi eliminati`);
    fetchData();
  };

  const getAlertBadge = (alert, label) => {
    if (alert === 'scaduta') {
      return <Badge variant="destructive" className="text-xs">{label} scaduta</Badge>;
    }
    if (alert === 'in_scadenza') {
      return <Badge className="bg-orange-100 text-orange-700 text-xs">{label} in scadenza</Badge>;
    }
    return null;
  };

  // Stats
  const totaleVeicoli = automezzi.length;
  const veicoliConAlert = automezzi.filter(a => 
    a.scadenza_assicurazione_alert || a.scadenza_bollo_alert || a.scadenza_revisione_alert
  ).length;

  // Riepilogo per CDC
  const riepilogoPerCdc = useMemo(() => {
    const stats = {};
    automezzi.forEach(v => {
      const cdcId = v.centro_costo_id || "senza_cdc";
      const cdcNome = v.centro_costo_nome || "Senza Locazione";
      if (!stats[cdcId]) {
        stats[cdcId] = {
          id: cdcId,
          nome: cdcNome,
          totale: 0,
          conAlert: 0
        };
      }
      stats[cdcId].totale += 1;
      if (v.scadenza_assicurazione_alert || v.scadenza_bollo_alert || v.scadenza_revisione_alert) {
        stats[cdcId].conAlert += 1;
      }
    });
    // Ordina per nome, mettendo "Senza Locazione" alla fine
    return Object.values(stats).sort((a, b) => {
      if (a.id === "senza_cdc") return 1;
      if (b.id === "senza_cdc") return -1;
      return a.nome.localeCompare(b.nome);
    });
  }, [automezzi]);

  // Colonne per DataTable
  const columns = [
    {
      key: 'veicolo',
      label: 'Veicolo',
      getValue: (row) => `${row.marca} ${row.modello}`,
      render: (value, row) => (
        <div>
          <span className="font-medium">{value}</span>
          {row.n_posti && <span className="text-xs text-muted-foreground ml-2">({row.n_posti} posti)</span>}
        </div>
      )
    },
    {
      key: 'targa',
      label: t('vehicles.plate'),
      render: (value) => <span className="font-mono font-medium">{value}</span>
    },
    {
      key: 'centro_costo_nome',
      label: 'Locazione',
      render: (value) => value || <span className="text-muted-foreground">-</span>
    },
    {
      key: 'ultimo_km',
      label: 'Ultimo KM',
      align: 'right',
      render: (value, row) => value ? (
        <div className="text-right">
          <span className="font-mono">{value.toLocaleString('it-IT')}</span>
          {row.data_ultimo_km && (
            <div className="text-xs text-muted-foreground">{row.data_ultimo_km}</div>
          )}
        </div>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'scadenza_assicurazione',
      label: 'Scad. Assicuraz.',
      render: (value, row) => {
        if (!value) return <span className="text-muted-foreground">-</span>;
        const alertClass = row.scadenza_assicurazione_alert === 'scaduta' ? 'text-red-600 font-medium' : 
                           row.scadenza_assicurazione_alert === 'in_scadenza' ? 'text-orange-600' : '';
        return (
          <div>
            <span className={alertClass}>{value}</span>
            {row.compagnia_assicuratrice && <div className="text-xs text-muted-foreground">{row.compagnia_assicuratrice}</div>}
          </div>
        );
      }
    },
    {
      key: 'scadenza_bollo',
      label: 'Scad. Bollo',
      render: (value, row) => {
        if (!value) return <span className="text-muted-foreground">-</span>;
        const alertClass = row.scadenza_bollo_alert === 'scaduta' ? 'text-red-600 font-medium' : 
                           row.scadenza_bollo_alert === 'in_scadenza' ? 'text-orange-600' : '';
        return <span className={alertClass}>{value}</span>;
      }
    },
    {
      key: 'scadenza_revisione',
      label: 'Scad. Revisione',
      render: (value, row) => {
        if (!value) return <span className="text-muted-foreground">-</span>;
        const alertClass = row.scadenza_revisione_alert === 'scaduta' ? 'text-red-600 font-medium' : 
                           row.scadenza_revisione_alert === 'in_scadenza' ? 'text-orange-600' : '';
        return <span className={alertClass}>{value}</span>;
      }
    },
    {
      key: 'prossimo_tagliando',
      label: 'Pross. Tagliando',
      align: 'right',
      render: (_, row) => {
        if (!row.km_prossimo_tagliando) return <span className="text-muted-foreground">-</span>;
        
        const kmMancanti = row.km_mancanti_tagliando;
        let alertClass = '';
        let prefix = '';
        
        if (row.km_tagliando_alert === 'scaduto') {
          alertClass = 'text-red-600 font-medium';
          prefix = 'Scaduto! ';
        } else if (row.km_tagliando_alert === 'in_scadenza') {
          alertClass = 'text-orange-600';
        }
        
        return (
          <div className="text-right">
            <div className="font-mono">{row.km_prossimo_tagliando.toLocaleString('it-IT')} km</div>
            <div className={`text-xs ${alertClass}`}>
              {prefix}{kmMancanti !== null && kmMancanti > 0 ? `Tra ${kmMancanti.toLocaleString('it-IT')} km` : kmMancanti !== null ? `${Math.abs(kmMancanti).toLocaleString('it-IT')} km fa` : ''}
            </div>
          </div>
        );
      }
    },
    {
      key: 'stato',
      label: t('common.status'),
      sortable: false,
      filterable: false,
      render: (_, row) => {
        const hasAlert = row.scadenza_assicurazione_alert || row.scadenza_bollo_alert || 
                         row.scadenza_revisione_alert || row.km_tagliando_alert;
        
        if (!hasAlert) {
          return <Badge variant="outline" className="text-green-600">OK</Badge>;
        }
        
        const isScaduto = row.scadenza_assicurazione_alert === 'scaduta' || 
                          row.scadenza_bollo_alert === 'scaduta' || 
                          row.scadenza_revisione_alert === 'scaduta' ||
                          row.km_tagliando_alert === 'scaduto';
        
        if (isScaduto) {
          return <Badge variant="destructive">{t('common.expired')}</Badge>;
        }
        
        return <Badge className="bg-orange-100 text-orange-700">Attenzione</Badge>;
      }
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            data-testid={`edit-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, item: row })}
            className="text-destructive"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automezzi-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("nav.fleet")}</h1>
        <p className="text-muted-foreground text-sm">Gestisci i veicoli aziendali</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Car className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Totale Veicoli</p>
                <p className="text-2xl font-bold tracking-tight">{totaleVeicoli}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Con Scadenze</p>
                <p className="text-2xl font-bold text-orange-600">{veicoliConAlert}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">In Regola</p>
                <p className="text-2xl font-bold text-green-600">{totaleVeicoli - veicoliConAlert}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riepilogo per Centro di Costo */}
      {riepilogoPerCdc.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building className="h-5 w-5 text-indigo-500" />{t('common.summaryByCostCenter')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {riepilogoPerCdc.map((cdc) => {
                const alertRate = cdc.totale > 0 ? Math.round((cdc.conAlert / cdc.totale) * 100) : 0;
                const colorClass = alertRate >= 50 ? 'border-red-200 bg-red-50' : 
                                   alertRate >= 25 ? 'border-orange-200 bg-orange-50' : 
                                   'border-green-200 bg-green-50';
                return (
                  <div key={cdc.id} className={`p-4 rounded-lg border ${colorClass}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-indigo-500" />
                        {cdc.nome}
                      </h4>
                      <Badge variant="outline" className="text-xs">
                        {cdc.totale} veicol{cdc.totale === 1 ? 'o' : 'i'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1">
                        <Car className="h-3 w-3 text-blue-500" />
                        <span className="text-muted-foreground">{t("common.total")}:</span>
                        <span className="font-medium">{cdc.totale}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-orange-500" />
                        <span className="text-muted-foreground">Alert:</span>
                        <span className="font-medium text-orange-600">{cdc.conAlert}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Veicoli</CardTitle>
          <CardDescription>Clicca su un veicolo per modificarlo</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={automezzi}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage="Nessun veicolo registrato"
            exportFileName="parco_automezzi"
            toolbar={
              canEditAutomezzi && (
                <Button onClick={openAddDialog} data-testid="add-automezzo-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Veicolo
                </Button>
              )
            }
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? t("vehicles.editVehicle") : t("vehicles.newVehicle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('vehicles.brand')} *</Label>
                <Input
                  value={form.marca}
                  onChange={(e) => setForm({ ...form, marca: e.target.value })}
                  placeholder="Es: Fiat"
                  data-testid="input-marca"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('vehicles.model')} *</Label>
                <Input
                  value={form.modello}
                  onChange={(e) => setForm({ ...form, modello: e.target.value })}
                  placeholder="Es: Ducato"
                  data-testid="input-modello"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('vehicles.plate')} *</Label>
                <Input
                  value={form.targa}
                  onChange={(e) => setForm({ ...form, targa: e.target.value.toUpperCase() })}
                  placeholder="Es: AB123CD"
                  className="uppercase"
                  data-testid="input-targa"
                />
              </div>
              <div className="space-y-2">
                <Label>N. Posti</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.n_posti}
                  onChange={(e) => setForm({ ...form, n_posti: e.target.value })}
                  placeholder="Es: 9"
                  data-testid="input-posti"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Locazione Attuale (Centro di Costo)</Label>
              <SearchableSelect
                options={[
                  { value: "__none__", label: "Nessuna" },
                  ...centriCosto.map(cc => ({ value: cc.id, label: cc.nome }))
                ]}
                value={form.centro_costo_id}
                onValueChange={(v) => setForm({ ...form, centro_costo_id: v })}
                placeholder="Seleziona ubicazione..."
                searchPlaceholder="Cerca centro costo..."
                emptyMessage="Nessun centro trovato"
                testId="select-centro-costo"
              />
            </div>
            <div className="space-y-2">
              <Label>Autista Principale</Label>
              <Input
                value={form.autista_principale}
                onChange={(e) => setForm({ ...form, autista_principale: e.target.value })}
                placeholder="Nome autista..."
                data-testid="input-autista"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder={t("common.notes")}
                data-testid="input-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={savingAutomezzo}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} data-testid="save-btn" disabled={savingAutomezzo}>
              {savingAutomezzo ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.create"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Eliminare il veicolo {deleteDialog.item?.marca} {deleteDialog.item?.modello} ({deleteDialog.item?.targa})?
              Verranno eliminati anche tutti i dati correlati (assicurazioni, bolli, km, manutenzioni).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })} disabled={deletingAutomezzo}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn" disabled={deletingAutomezzo}>
              {deletingAutomezzo ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
